import {
  formatDistanceToNow,
  isToday,
  isYesterday,
  differenceInCalendarDays,
} from 'date-fns';
import { AlertCircle, BellOff, Check, CheckCheck, X } from 'lucide-react';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useNotificationsPanel } from '../hooks/useNotificationsPanel';
import type { NotificationItem } from '../services/notifications.service';
import { notificationVisual } from '../utils/notification-visuals';

type Bucket = 'Today' | 'Yesterday' | 'Earlier this week' | 'Older';
const BUCKET_ORDER: Bucket[] = ['Today', 'Yesterday', 'Earlier this week', 'Older'];

function bucketFor(iso: string): Bucket {
  const d = new Date(iso);
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return differenceInCalendarDays(new Date(), d) <= 7 ? 'Earlier this week' : 'Older';
}

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

export function NotificationsPanel() {
  const {
    filter,
    setFilter,
    items,
    unreadCount,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    markRead,
    markAll,
    isMarkingAll,
    deleteNotification,
  } = useNotificationsPanel();

  const grouped = BUCKET_ORDER.map((bucket) => ({
    bucket,
    rows: items.filter((n) => bucketFor(n.createdAt) === bucket),
  })).filter((g) => g.rows.length > 0);

  // Clicking a row marks it read (no navigation — notification metadata does
  // not carry reliable in-app routes).
  const openNotification = (n: NotificationItem) => {
    if (n.notificationStatus === 'DELIVERED') markRead(n.id);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-6 pb-4 pt-6">
        <div>
          <h2 className="text-lg font-semibold text-text-heading">Notifications</h2>
          <p className="mt-0.5 text-sm text-text-body">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
              : "You're all caught up"}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs"
          disabled={unreadCount === 0 || isMarkingAll}
          onClick={() => markAll()}
        >
          <CheckCheck className="size-3.5" />
          Mark all read
        </Button>
      </div>

      <div className="px-6">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'unread')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="unread">
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Separator className="mt-4" />

      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_unused, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl px-3 py-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertCircle className="size-6" />
            </span>
            <div>
              <p className="text-sm font-medium text-text-heading">
                Couldn't load notifications
              </p>
              <p className="mt-1 text-xs text-text-body">
                Something went wrong. Try again.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-text-body">
              <BellOff className="size-6" />
            </span>
            <div>
              <p className="text-sm font-medium text-text-heading">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
              <p className="mt-1 text-xs text-text-body">
                {filter === 'unread'
                  ? "You've read everything — nice."
                  : 'Updates about your work will show up here.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="px-3 py-3">
            {grouped.map((group) => (
              <div key={group.bucket} className="mb-2">
                <p className="px-3 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-text-body/70">
                  {group.bucket}
                </p>
                <div className="space-y-1">
                  {group.rows.map((n) => {
                    const unread = n.notificationStatus === 'DELIVERED';
                    const { icon: Icon, chipClassName } = notificationVisual(n.type);
                    return (
                      <div
                        key={n.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openNotification(n)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openNotification(n);
                          }
                        }}
                        className={cn(
                          'group relative flex cursor-pointer items-start gap-3 rounded-xl px-3 py-3 transition-colors',
                          unread ? 'bg-primary-50/40 hover:bg-primary-50/70' : 'hover:bg-muted/60',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                            chipClassName,
                          )}
                        >
                          <Icon className="size-4" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <p
                            className={cn(
                              'truncate text-[13px] leading-snug text-text-heading',
                              unread ? 'font-semibold' : 'font-medium',
                            )}
                          >
                            {n.subject}
                          </p>
                          {n.content ? (
                            <p className="mt-0.5 line-clamp-1 text-xs text-text-body">
                              {n.content}
                            </p>
                          ) : null}
                          <p className="mt-1 text-[11px] text-text-body/80">
                            {relativeTime(n.createdAt)}
                          </p>
                        </div>

                        {/* Unread dot */}
                        {unread ? (
                          <span className="mt-2 size-2 shrink-0 rounded-full bg-primary group-hover:opacity-0" />
                        ) : null}

                        {/* Hover actions */}
                        <div className="absolute right-2 top-2 hidden items-center gap-0.5 group-hover:flex">
                          {unread ? (
                            <button
                              type="button"
                              title="Mark as read"
                              aria-label="Mark as read"
                              onClick={(e) => {
                                e.stopPropagation();
                                markRead(n.id);
                              }}
                              className="rounded-md p-1 text-text-body hover:bg-background hover:text-primary"
                            >
                              <Check className="size-3.5" />
                            </button>
                          ) : null}
                          {/* Deleting is permanent — the backend has no
                              DISMISSED status and no soft delete. */}
                          <ConfirmDialog
                            title="Delete this notification?"
                            description={`"${n.subject}" will be removed permanently. This cannot be undone.`}
                            confirmLabel="Delete"
                            variant="destructive"
                            onConfirm={() => deleteNotification(n.id)}
                            trigger={
                              <button
                                type="button"
                                title="Delete"
                                aria-label="Delete"
                                onClick={(e) => e.stopPropagation()}
                                className="rounded-md p-1 text-text-body hover:bg-background hover:text-destructive"
                              >
                                <X className="size-3.5" />
                              </button>
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {hasNextPage ? (
              <div className="px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load older notifications'}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
